import { FC, useContext } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { EQueryStringParam, ERoutes, EValidHandoffScreens } from '../../util/constants';
import LoginScreen from '../../screens/LoginScreen/LoginScreen';
import ForgotPasswordScreen from '../../screens/ForgotPasswordScreen/ForgotPasswordScreen';
import ResetPasswordScreen from '../../screens/ResetPasswordScreen/ResetPasswordScreen';
import GlobalContext, { GlobalProvider } from '../../contexts/GlobalContext';
import Register3rdPartyScreen from '../../screens/Register3rdPartyScreen/Register3rdPartyScreen';
import ErrorScreen from '../../screens/ErrorScreen/ErrorScreen';
import VerifyAccountScreen from '../../screens/VerifyAccountScreen/VerifyAccountScreen';
import ChangeEmailScreen from '../../screens/ChangeEmailScreen/ChangeEmailScreen';
import CreateAccountScreen from '../../screens/CreateAccountScreen/CreateAccountScreen';
import CreateFamilyAccountScreen from '../../screens/CreateFamilyAccountScreen/CreateFamilyAccountScreen';
import UnverifiedAccountScreen from '../../screens/UnverifiedAccountScreen/UnverifiedAccountScreen';
import UnsupportedBrowserScreen from '../../screens/UnspportedBrowserScreen/UnsupportedBrowserScreen';
import { checkBrowserIsSupported } from '../../util/helpers';
import { TBrowserSupport } from '../../util/types';
import styles from './Identity.module.scss';
import MigrateAccountScreen from '../../screens/MigrateAccountScreen/MigrateAccountScreen';
import MigrateToExistingAccountScreen from '../../screens/MigrateToExistingAccountScreen/MigrateToExistingAccountScreen';
import GraduateUserScreen from '../../screens/GraduateUserScreen/GraduateUserScreen';
import ConfirmAccountScreen from '../../screens/ConfirmAccountScreen/ConfirmAccountScreen';
import TermsScreen from '../../screens/TermsScreen/TermsScreen';
import VerifyStaffAccountScreen from '../../screens/VerifyStaffAccountScreen/VerifyStaffAccountScreen';
import ForgotPasswordStaffScreen from '../../screens/ForgotPasswordStaffScreen/ForgotPasswordStaffScreen';
import ResetPasswordStaffScreen from '../../screens/ResetPasswordStaffScreen/ResetPasswordStaffScreen';
import ChangeEmailStaffScreen from '../../screens/ChangeEmailStaffScreen/ChangeEmailStaffScreen';
import UnverifiedStaffAccountScreen from '../../screens/UnverifiedStaffAccountScreen/UnverifiedStaffAccountScreen';
import ConfirmStaffAccountScreen from '../../screens/ConfirmStaffAccountScreen/ConfirmStaffAccountScreen';
import ConsentScreen from '../../screens/ConsentScreen/ConsentScreen';
import MigrateStaffAccountScreen from '../../screens/MigrateStaffAccountScreen/MigrateStaffAccountScreen';
import MultiFactorAuthScreen from '../../screens/MultiFactorAuthScreen/MultiFactorAuthScreen';
import LogoutScreen from '../../screens/LogoutScreen/LogoutScreen';
import { CssBaseline } from '@mui/material';
import FacebookAddPasswordScreen from '../../screens/FacebookAddPasswordScreen/FacebookAddPasswordScreen';
import ThirdPartyLoginScreen from '../../screens/ThirdPartyLoginScreen/ThirdPartyLoginScreen';

const AppUi: FC = () => {
    const { entryPointUrl } = useContext(GlobalContext);
    return (
        <Switch>
            <Route path={ERoutes.login} component={LoginScreen} />
            <Route path={ERoutes.thirdPartyLogin} component={ThirdPartyLoginScreen} />
            <Route path={ERoutes.logout} component={LogoutScreen} />
            <Route path={ERoutes.facebookAddPassword} component={FacebookAddPasswordScreen} />
            <Route exact path={ERoutes.terms} component={TermsScreen} />
            <Route exact path={ERoutes.consent} component={ConsentScreen} />
            <Route path={ERoutes.multiFactorAuth} component={MultiFactorAuthScreen} />
            <Route exact path={ERoutes.forgotPassword} component={ForgotPasswordScreen} />
            <Route exact path={ERoutes.forgotPasswordStaff} component={ForgotPasswordStaffScreen} />
            <Route exact path={ERoutes.resetPassword} component={ResetPasswordScreen} />
            <Route exact path={ERoutes.resetPasswordStaff} component={ResetPasswordStaffScreen} />
            <Route path={ERoutes.createAccount}>
                {entryPointUrl.path === ERoutes.createAccount && entryPointUrl.isAtEntryPoint ? (
                    <Redirect
                        to={{
                            pathname: ERoutes.login,
                            search: `${EQueryStringParam.handoffScreen}=${EValidHandoffScreens.createAccount}`,
                        }}
                    />
                ) : (
                    <CreateAccountScreen />
                )}
            </Route>
            <Route path={ERoutes.createFamilyAccount} component={CreateFamilyAccountScreen} />
            <Route path={ERoutes.register} component={Register3rdPartyScreen} />
            <Route exact path={ERoutes.verify} component={VerifyAccountScreen} />
            <Route exact path={ERoutes.verifyStaff} component={VerifyStaffAccountScreen} />
            <Route exact path={ERoutes.unverified} component={UnverifiedAccountScreen} />
            <Route exact path={ERoutes.unverifiedStaff} component={UnverifiedStaffAccountScreen} />
            <Route exact path={ERoutes.changeEmail} component={ChangeEmailScreen} />
            <Route exact path={ERoutes.changeEmailStaff} component={ChangeEmailStaffScreen} />
            <Route path={ERoutes.migrateAccount} component={MigrateAccountScreen} />
            <Route path={ERoutes.staffMigrateAccount} component={MigrateStaffAccountScreen} />
            <Route path={ERoutes.migrateToExistingAccount} component={MigrateToExistingAccountScreen} />
            <Route path={ERoutes.graduateUser} component={GraduateUserScreen} />
            <Route path={ERoutes.confirmAccount} component={ConfirmAccountScreen} />
            <Route path={ERoutes.confirmAccountStaff} component={ConfirmStaffAccountScreen} />
            <Route exact path={ERoutes.error} component={ErrorScreen} />
            <Route component={ErrorScreen} />
        </Switch>
    );
};

const Identity: FC = () => {
    const browser: TBrowserSupport = checkBrowserIsSupported();
    return browser.isSupported ? (
        <GlobalProvider>
            <CssBaseline enableColorScheme />
            <div className={styles.root}>
                <AppUi />
            </div>
        </GlobalProvider>
    ) : (
        <UnsupportedBrowserScreen reason={browser.unSupportedReason} />
    );
};

export default Identity;
